import React, { Suspense, lazy } from "react"
import { Router, Switch, Route, Redirect } from "react-router-dom"
import { history } from "./history"
import { connect } from "react-redux"
import Spinner from "./components/@vuexy/spinner/Loading-spinner"
import { ContextLayout } from "./utility/context/Layout"
import { getUser } from "./helpers/AuthHelper"
import { canClassUseWritingFeature } from "./helpers/Constants"

// Route-based code splitting
const Login = lazy(() => import("./views/pages/Login"))
const Logout = lazy(() => import("./views/pages/Logout"))
const Signup = lazy(() => import("./views/pages/Signup"))
const InvitationSignup = lazy(() => import("./views/pages/InvitationSignup"))
const Home = lazy(() => import("./views/pages/Autorized/Home"))
const StudentsList = lazy(() => import("./views/pages/Autorized/StudentsList"))
const StudentProfile = lazy(() =>
  import("./views/pages/Autorized/StudentProfile")
)
const StudentsDashboard = lazy(() =>
  import("./views/pages/Autorized/StudentsDashboard")
)
const StudentBooks = lazy(() => import("./views/pages/Autorized/StudentBooks"))
const Achievements = lazy(() => import("./views/pages/Autorized/Achievements"))
const Reports = lazy(() => import("./views/pages/Autorized/Reports"))
const Classwork = lazy(() => import("./views/pages/Autorized/Classwork"))
const AssignBooks = lazy(() => import("./views/pages/Autorized/AssignBooks"))
const ViewBook = lazy(() => import("./views/pages/Autorized/ViewBook"))
const AssignedBooks = lazy(() =>
  import("./views/pages/Autorized/AssignedBooks")
)
const AssignedBookReport = lazy(() =>
  import("./views/pages/Autorized/AssignedBookReport")
)
const ReadingComprehensionReport = lazy(() =>
  import("./views/pages/Autorized/ReadingComprehensionReport")
)
const Report = lazy(() => import("./views/pages/Autorized/Report"))
const ReportDecodings = lazy(() =>
  import("./views/pages/Autorized/ReportDecodings")
)
const ImplementationGuides = lazy(() =>
  import("./views/pages/Autorized/ImplementationGuides")
)
const VocabularyUnits = lazy(() =>
  import("./views/pages/Autorized/VocabularyUnits")
)
const VocabularyAssign = lazy(() =>
  import("./views/pages/Autorized/VocabularyAssign")
)
const VocabularyAssigned = lazy(() =>
  import("./views/pages/Autorized/VocabularyAssigned")
)
const VocabularyTimeline = lazy(() =>
  import("./views/pages/Autorized/VocabularyTimeline")
)
const ViewLesson = lazy(() => import("./views/pages/Autorized/ViewLesson"))
const GrammarUnits = lazy(() => import("./views/pages/Autorized/GrammarUnits"))
const GrammarAssign = lazy(() =>
  import("./views/pages/Autorized/GrammarAssign")
)
const GrammarAssigned = lazy(() =>
  import("./views/pages/Autorized/GrammarAssigned")
)
const GrammarTimeline = lazy(() =>
  import("./views/pages/Autorized/GrammarTimeline")
)
const ViewGrammarLesson = lazy(() =>
  import("./views/pages/Autorized/ViewGrammarLesson")
)
const ConversationReport = lazy(() =>
  import("./views/pages/Autorized/ConversationReport")
)
const ConversationTimeline = lazy(() =>
  import("./views/pages/Autorized/ConversationTimeline")
)
const WritingAssign = lazy(() =>
  import("./views/pages/Autorized/WritingAssign")
)
const WritingAssigned = lazy(() =>
  import("./views/pages/Autorized/WritingAssigned")
)
const WritingAssignedInfo = lazy(() =>
  import("./views/pages/Autorized/WritingAssignedInfo")
)
const WritingStudentReport = lazy(() =>
  import("./views/pages/Autorized/WritingStudentReport")
)

const Error500 = lazy(() => import("./views/errors/error500"))
const Error404 = lazy(() => import("./views/errors/error404"))

// Set Layout and Component Using App Route
const RouteConfig = ({
  component: Component,
  fullLayout,
  permission,
  user,
  ...rest
}) => (
  <Route
    {...rest}
    render={(props) => {
      return (
        <ContextLayout.Consumer>
          {(context) => {
            let LayoutTag =
              fullLayout === true
                ? context.fullLayout
                : context.state.activeLayout === "horizontal"
                ? context.horizontalLayout
                : context.VerticalLayout
            return (
              <LayoutTag {...props} permission={props.user}>
                <Suspense fallback={<Spinner />}>
                  <Component {...props} />
                </Suspense>
              </LayoutTag>
            )
          }}
        </ContextLayout.Consumer>
      )
    }}
  />
)
const mapStateToProps = (state) => {
  return {
    user: state.user,
  }
}

const AppRoute = connect(mapStateToProps)(RouteConfig)

class AppRouter extends React.Component {
  render() {
    const user = JSON.parse(getUser() ?? "{}");
    const showWriting = user?.user?.classes?.some(
      (teacherClass) => canClassUseWritingFeature(teacherClass)
    );
    // TODO: revisar el env, porque en prod sigue diciendo "development"
    // const isProduction = process.env.NODE_ENV === "production"
    // console.log({ isProduction })
    return (
      // Set the directory path if you are deploying in sub-folder
      <Router history={history}>
        <Switch>
          <AppRoute exact path="/" component={Home} />
          <AppRoute exact path="/success" component={StudentsList} />
          <AppRoute exact path="/students" component={StudentsList} />
          <AppRoute exact path="/student" component={StudentProfile} />
          <AppRoute
            exact
            path="/students-dashboard"
            component={StudentsDashboard}
          />
          <AppRoute exact path="/student-books" component={StudentBooks} />
          <AppRoute exact path="/achievements" component={Achievements} />
          <AppRoute exact path="/reports" component={Reports} />
          <AppRoute exact path="/classwork" component={Classwork} />
          <AppRoute exact path="/assign-books" component={AssignBooks} />
          <AppRoute
            exact
            path="/view-book/:bookId"
            component={ViewBook}
            fullLayout
          />
          <AppRoute exact path="/assigned-books" component={AssignedBooks} />
          <AppRoute
            exact
            path="/assigned-book-report"
            component={AssignedBookReport}
          />
          <AppRoute
            exact
            path="/reading-comprehension-report"
            component={ReadingComprehensionReport}
          />
          <AppRoute exact path="/report" component={Report} />
          <AppRoute exact path="/report-decoding" component={ReportDecodings} />
          <AppRoute exact path="/guides" component={ImplementationGuides} />
          <AppRoute
            exact
            path="/vocabulary-units"
            component={VocabularyUnits}
          />
          <AppRoute
            exact
            path="/vocabulary-assign"
            component={VocabularyAssign}
          />
          <AppRoute
            exact
            path="/vocabulary-assigned"
            component={VocabularyAssigned}
          />
          <AppRoute
            exact
            path="/vocabulary-timeline"
            component={VocabularyTimeline}
          />
          <AppRoute
            exact
            path="/lesson/:subunitId/:session"
            component={ViewLesson}
            fullLayout
          />
          <AppRoute exact path="/grammar-units" component={GrammarUnits} />
          <AppRoute exact path="/grammar-assign" component={GrammarAssign} />
          <AppRoute
            exact
            path="/grammar-assigned"
            component={GrammarAssigned}
          />
          <AppRoute
            exact
            path="/grammar-timeline"
            component={GrammarTimeline}
          />
          <AppRoute
            exact
            path="/grammar-lesson/:subunitId/:session"
            component={ViewGrammarLesson}
            fullLayout
          />
          <AppRoute
            exact
            path="/report-conversation"
            component={ConversationReport}
          />
          <AppRoute
            exact
            path="/conversation-timeline"
            component={ConversationTimeline}
          />
          {showWriting && (
            <>
              <AppRoute
                exact
                path="/writing-assign"
                component={WritingAssign}
              />
              <AppRoute
                exact
                path="/writing-assigned"
                component={WritingAssigned}
              />
              <AppRoute
                exact
                path="/writing-assigned-info"
                component={WritingAssignedInfo}
              />
              <AppRoute
                exact
                path="/writing-student-report"
                component={WritingStudentReport}
              />
            </>
          )}
          <AppRoute path="/login" component={Login} fullLayout />
          // TODO: hacer que la condicion sea por el environment
          <AppRoute path="/logout" component={Logout} fullLayout />
          {false ? (
            <AppRoute path="/signup/:step?" component={Signup} fullLayout />
          ) : (
            <Redirect to="/" />
          )}
          {false ? (
            <AppRoute
              path="/invitation-signup/:token"
              component={InvitationSignup}
              fullLayout
            />
          ) : (
            <Redirect to="/" />
          )}
          <AppRoute path="/500" component={Error500} fullLayout />
          <AppRoute component={Error404} fullLayout />
        </Switch>
      </Router>
    )
  }
}

export default AppRouter
